<template>
  <div class="ai-chat-page">
    <Breadcrumbs main="AI Чат" title="Диалог"/>

    <AccessDenied v-if="forbidden"/>

    <div v-else class="ai-chat-shell">
      <aside class="ai-sidebar">
        <SessionsList
            :sessions="sessions"
            :active-id="activeId"
            :loading="loadingList"
            :creating="creating"
            @create="onCreate"
            @select="onSelect"
            @delete="onDelete"
        />
      </aside>
      <main class="ai-main">
        <ChatWindow
            :session-id="activeId"
            @stream-start="onStreamStart"
            @stream-end="onStreamEnd"
        />
      </main>
    </div>
  </div>
</template>

<script>
import api from '@/api/api';
import { canUseAiChat, permissionsKnown } from '@/common/permissions';
import SessionsList from '@/components/AiChat/SessionsList.vue';
import ChatWindow from '@/components/AiChat/ChatWindow.vue';
import AccessDenied from '@/components/AiChat/AccessDenied.vue';

export default {
  name: 'AiChatPage',
  components: { SessionsList, ChatWindow, AccessDenied },
  data() {
    return {
      sessions: [],
      activeId: null,
      loadingList: false,
      creating: false,
    };
  },
  computed: {
    forbidden() {
      return permissionsKnown() && !canUseAiChat();
    },
  },
  created() {
    const sid = this.$route.query.session;
    if (sid) this.activeId = Number(sid) || sid;
    this.loadSessions();
  },
  watch: {
    activeId(id) {
      const current = this.$route.query.session;
      if (id && String(id) !== String(current)) {
        this.$router.replace({ query: { ...this.$route.query, session: id } });
      } else if (!id && current) {
        const q = { ...this.$route.query };
        delete q.session;
        this.$router.replace({ query: q });
      }
    },
  },
  methods: {
    async loadSessions() {
      this.loadingList = true;
      try {
        const resp = await api.aiChat.getSessions({ per_page: 50 });
        this.sessions = (resp && resp.data && resp.data.data) || [];
      } catch (e) {
        void e;
      } finally {
        this.loadingList = false;
      }
    },

    async onCreate() {
      this.creating = true;
      try {
        const resp = await api.aiChat.createSession({ title: null });
        const s = (resp && resp.data && resp.data.data) || resp.data;
        if (s && s.id) {
          this.sessions = [s, ...this.sessions];
          this.activeId = s.id;
        }
      } catch (e) {
        void e;
      } finally {
        this.creating = false;
      }
    },

    onSelect(id) { this.activeId = id; },

    async onDelete(id) {
      try {
        await api.aiChat.deleteSession(id);
        this.sessions = this.sessions.filter(s => s.id !== id);
        if (this.activeId === id) this.activeId = null;
      } catch (e) {
        void e;
      }
    },

    onStreamStart() {},
    onStreamEnd() { this.loadSessions(); },
  },
};
</script>

<style lang="scss">
.ai-chat-page {
  --ai-bg: #ffffff;
  --ai-sidebar: #f9f9f9;
  --ai-sidebar-hover: #ececec;
  --ai-sidebar-active: #e3e3e3;
  --ai-border: #e5e5e5;
  --ai-text: #0d0d0d;
  --ai-text-muted: #6e6e80;
  --ai-text-subtle: #8e8ea0;
  --ai-user-bubble: #f4f4f4;
  --ai-accent: #0d0d0d;
  --ai-accent-fg: #ffffff;
  --ai-danger: #ef4444;
  --ai-radius: 16px;
  --ai-radius-pill: 28px;
  --ai-font: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, "Helvetica Neue", Arial, sans-serif;
  --ai-font-mono: ui-monospace, "SF Mono", "JetBrains Mono", "Fira Code", Menlo, Consolas, monospace;
  font-family: var(--ai-font);
  color: var(--ai-text);
}
</style>

<style scoped lang="scss">
.ai-chat-shell {
  display: grid;
  grid-template-columns: 260px 1fr;
  height: calc(100vh - 220px);
  min-height: 520px;
  background: var(--ai-bg);
  border: 1px solid var(--ai-border);
  border-radius: 12px;
  overflow: hidden;
  margin: 0 1rem 1rem;
}
.ai-sidebar {
  background: var(--ai-sidebar);
  border-right: 1px solid var(--ai-border);
  min-width: 0;
  min-height: 0;
  height: 100%;
}
.ai-main {
  background: var(--ai-bg);
  min-width: 0;
  min-height: 0;
  height: 100%;
}

@media (max-width: 768px) {
  .ai-chat-shell {
    grid-template-columns: 1fr;
    height: auto;
  }
  .ai-sidebar { height: 220px; border-right: none; border-bottom: 1px solid var(--ai-border); }
  .ai-main { height: 70vh; }
}
</style>
